import { useCallback } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useUser } from '@/hooks/user/useUser';
import { useLazyGetConversationHistoryQuery, useLazyGetConversationFeedbackQuery } from '@/services/ApiService';
import { useChatMachine } from '@/machines/useChatMachine';
import { useAiChatPanel } from '@/hooks/aiChat/useAiChatPanel';
import { useOnboardingComplete } from '@/hooks/aiChat/useOnboardingComplete';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { mapHistoryToChatMessages, mapFeedbackToRecord } from './chatDataMappers';
import { ChatPanelHeader } from './ChatPanelHeader';
import { SidebarDrawer } from './SidebarDrawer';
import { ConversationSidebar } from './ConversationSidebar';
import { ChatMessageArea } from './ChatMessageArea';
import { ChatOnboarding } from './Onboarding/ChatOnboarding';

export const AiChatPanel = () => {
    const {
        isOpen,
        historySidebarOpen,
        setIsOpen,
        closeSidebar,
    } = useAiChatPanel();
    const isDesktop = useIsDesktop();
    const { user } = useUser();
    const { isComplete: isOnboardingComplete, markComplete: markOnboardingComplete } = useOnboardingComplete(user?.id ?? '');
    const { conversationId, loadConversation, startNewConversation } = useChatMachine();
    const [getConversationHistory] = useLazyGetConversationHistoryQuery();
    const [getConversationFeedback] = useLazyGetConversationFeedbackQuery();

    const handleSelectConversation = useCallback(async (selectedConversationId: string) => {
        if (selectedConversationId === conversationId) {
            if (!isDesktop) closeSidebar();
            return;
        }

        try {
            const [historyResult, feedbackResult] = await Promise.all([
                getConversationHistory(selectedConversationId).unwrap(),
                getConversationFeedback(selectedConversationId).unwrap(),
            ]);

            const chatMessages = mapHistoryToChatMessages(historyResult?.messages);
            const feedbackMap = mapFeedbackToRecord(feedbackResult?.feedback ?? []);

            loadConversation(selectedConversationId, chatMessages, feedbackMap);
            if (!isDesktop) closeSidebar();
        }
        catch {
            toast.error('Failed to load conversation. Please try again.');
        }
    }, [conversationId, getConversationHistory, getConversationFeedback, loadConversation, isDesktop, closeSidebar]);

    const handleNewConversation = useCallback(() => {
        startNewConversation();
        if (!isDesktop) closeSidebar();
    }, [startNewConversation, isDesktop, closeSidebar]);

    if (!user) {
        return null;
    }

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side={isDesktop ? 'right' : 'bottom'}
                className={cn(
                    'bg-secondary border-none light:bg-background dark:bg-blue-700 flex flex-col p-0 overflow-visible',
                    isDesktop
                        ? cn(
                                'h-full w-[600px] sm:max-w-[600px]',
                                historySidebarOpen
                                    ? 'rounded-tl-none rounded-bl-none'
                                    : 'rounded-tl-xl rounded-bl-xl',
                            )
                        : 'rounded-t-xl h-[90vh] max-h-[90vh]',
                )}
            >
                {isDesktop && (
                    <SidebarDrawer isOpen={historySidebarOpen}>
                        <ConversationSidebar
                            currentConversationId={conversationId}
                            onSelectConversation={handleSelectConversation}
                            onNewConversation={handleNewConversation}
                        />
                    </SidebarDrawer>
                )}

                {!isDesktop && historySidebarOpen
                    ? (
                            <ConversationSidebar
                                currentConversationId={conversationId}
                                onSelectConversation={handleSelectConversation}
                                onNewConversation={handleNewConversation}
                                onClose={closeSidebar}
                            />
                        )
                    : (
                            <div className="flex flex-col h-full">
                                <ChatPanelHeader />
                                <div className="flex-1 min-h-0 overflow-hidden px-4 lg:px-6">
                                    <ChatMessageArea />
                                </div>
                            </div>
                        )}
                {!isOnboardingComplete && (
                    <ChatOnboarding onComplete={markOnboardingComplete} />
                )}
            </SheetContent>
        </Sheet>
    );
};
